import { useEffect, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';

export interface SearchInputProps {
  /** Current search text (controlled). */
  value: string;
  /** Fired with the new text — debounced when `delay` is set. */
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * Debounce in ms before `onChange` fires. The field itself always updates
   * immediately, so typing never feels laggy; only the caller's fetch waits.
   * 0 disables debouncing.
   */
  delay?: number;
  /** Width of the control; a number is treated as px. */
  width?: number | string;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  testId?: string;
}

/**
 * The search field used across the admin screens.
 *
 * Replaces the six different hand-rolled versions that existed before — each
 * with its own icon offset, size and width. Two things it fixes that are easy to
 * reintroduce:
 *
 *  · `p-input-icon-left` was deprecated in PrimeReact 10 and no longer positions
 *    its icon, so the magnifier rendered on top of the placeholder.
 *  · `index.css` sets `.p-inputtext { padding: 6px 10px !important }`, and an
 *    !important stylesheet rule outranks an inline style attribute — so the room
 *    for the icon CANNOT be reserved with `style={{ paddingLeft }}`. It comes
 *    from `p-inputtext-left-icon` (and `has-trail-action` for the clear button)
 *    in index.css instead.
 *
 * Beyond the fix it adds what a search box is expected to do: a clear button
 * once there is text, Escape to clear, Enter to commit past the debounce.
 */
const SearchInput = ({
  value,
  onChange,
  placeholder = 'Search...',
  delay = 0,
  width = 240,
  className = '',
  disabled,
  autoFocus,
  testId,
}: SearchInputProps) => {
  const [text, setText] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // What we last told the parent. Lets an external reset (a "clear filters"
  // button) flow back in, without the echo of our own debounced emit fighting
  // the characters still being typed.
  const emitted = useRef(value);

  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const push = (next: string, immediate = false) => {
    setText(next);
    if (timer.current) clearTimeout(timer.current);
    const fire = () => { emitted.current = next; onChange(next); };
    if (immediate || !delay) fire();
    else timer.current = setTimeout(fire, delay);
  };

  return (
    <div
      className={`relative inline-flex items-center ${className}`}
      style={{ width: typeof width === 'number' ? `${width}px` : width }}
    >
      <i
        className="pi pi-search pointer-events-none absolute left-3 text-ink-muted"
        style={{ fontSize: '0.8rem' }}
      />
      <InputText
        value={text}
        onChange={(e) => push(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && text) { e.stopPropagation(); push('', true); }
          // commit straight away rather than waiting out the debounce
          if (e.key === 'Enter') push(text, true);
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        // deliberately not type="search": WebKit renders its own clear cross,
        // which would sit next to ours
        type="text"
        role="searchbox"
        aria-label={placeholder}
        data-testid={testId}
        className={`w-full text-sm p-inputtext-left-icon${text ? ' has-trail-action' : ''}`}
      />
      {text && !disabled && (
        <button
          type="button"
          onClick={() => push('', true)}
          aria-label="Clear search"
          data-testid={testId ? `${testId}.Clear` : undefined}
          className="absolute right-2 flex h-5 w-5 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
        >
          <i className="pi pi-times" style={{ fontSize: '0.7rem' }} />
        </button>
      )}
    </div>
  );
};

export default SearchInput;
